import { useTranslation } from 'react-i18next';
import { Row, Col, Button, FormGroup, Label, Input } from 'reactstrap';
import { getPassedExamsRadios, getPassedScoreRadios } from '../../../../constants';
import Select from 'react-select';
import { getContrastColor } from '../../../../utils';
import { MultiValueContainer, SelectOption } from '../../../uiKits/chip/Chip';

export const LeadersFilters = ({
  setSelectedPassedExam,
  setFilters,
  tabItem,
  selectedPassedExam,
  filters,
    applyFilters,
    conditions,
    statuses,
}: {
  applyFilters?: () => void;
  setSelectedPassedExam: (value: string) => void;
  tabItem: { id: number; title: string };
  selectedPassedExam: string;
  filters?: {
    tabId: number;
    passedExams: string;
    passedEqualityKey: string;
    score: string;
    scoreEqualityKey: string;
    scoreQty: string;
    statusFilter: number[];
    passedExamsQty: string;
  };
  setFilters?: React.Dispatch<
    React.SetStateAction<{
      tabId: number;
      passedExams: string;
      passedEqualityKey: string;
      score: string;
      scoreEqualityKey: string;
      scoreQty: string;
      statusFilter: number[];
      passedExamsQty: string;
    }>
  >;
        conditions?: { value: string; label: string }[];
  statuses: { value: number; label: string; colorCode?: string }[];
}) => {
  const { t } = useTranslation();
  const colourStyles = {
    option: (provided: any, state: any) => {
      const hex = state?.data?.colorCode ?? provided.backgroundColor ?? '#dddddd';
      const translucent = /^#([0-9A-Fa-f]{6})$/.test(hex) ? `${hex}80` : hex;
      return {
        ...provided,
        // show translucent background only when focused/hovered; keep selected option transparent
        backgroundColor: state.isFocused ? translucent : 'transparent',
        color: state.isFocused ? getContrastColor(state.data?.colorCode) : provided.color,
      };
    },
    // Make the chip background a translucent variant of the color so the
    // small color swatch (rendered inside the chip) remains visible.
    multiValue: (provided: any, state: any) => {
      const hex = state.data.colorCode;
      const luminance = hex
        ? (() => {
            try {
              const cleaned = hex.replace('#', '');
              const r = parseInt(cleaned.substring(0, 2), 16);
              const g = parseInt(cleaned.substring(2, 4), 16);
              const b = parseInt(cleaned.substring(4, 6), 16);
              return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
            } catch (e) {
              return 1;
            }
          })()
        : 1;
      const borderColor = luminance > 0.6 ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.14)';
      return {
        ...provided,
        backgroundColor: hex ? `${hex}80` : provided.backgroundColor,
        border: `1px solid ${borderColor}`,
        boxShadow: 'inset 0 -1px 0 rgba(0,0,0,0.04)',
      };
    },
    // Label text should contrast with the underlying solid color swatch,
    // but keep readable over the translucent chip background.
    multiValueLabel: (provided: any, state: any) => ({
      ...provided,
      // color: getContrastColor(state.data.colorCode),
      paddingRight: 6,
    }),
    control: (provided: any) => ({ ...provided, minHeight: '38px' }),
  };
  return (
    <Row className="mb-3">
      <Col sm="12" className="mb-3">
        <h5>{t('exams.totalLeaderBoard.filters.title')}</h5>
      </Col>
      <Col sm="3">
        <p>{t('exams.totalLeaderBoard.filters.passedExams')}</p>
        {getPassedExamsRadios(t).map((input) => (
          <FormGroup check key={input.id} className={`radio radio-'primary'`}>
            <Input
              type="radio"
              name={`${input.name}-${tabItem.id}`}
              id={`radio-${tabItem.id}-${input.id}`}
              value={input.value}
              checked={selectedPassedExam === input.value}
              onChange={() => {
                setSelectedPassedExam(input.value);
                if (input.value === 'all') {
                  // reset dependent filters
                  setFilters &&
                    setFilters((prev) => ({
                      ...prev,
                      tabId: tabItem.id,
                      passedExams: input.value,
                      passedEqualityKey: 'equal',
                      passedExamsQty: '0',
                    }));
                  return;
                } else
                  setFilters &&
                    setFilters((prev) => ({
                      ...prev,
                      tabId: tabItem.id,
                      passedExams: input.value,
                    }));
              }}
            />
            <Label check htmlFor={`radio-${tabItem.id}-${input.id}`}>
              {input.text}
            </Label>
          </FormGroup>
        ))}

        <>
          {filters && filters.tabId === tabItem.id && filters.passedExams !== 'all' && (
            <div className="d-flex gap-3 pe-3">
              <Col sm="8">
                <Select<{ value: string; label: string }, false>
                  options={conditions}
                  closeMenuOnSelect={true}
                  className="custom-selector no_placeholder"
                  classNamePrefix={'custom-selector'}
                  // styles={colourStyles}
                  formatOptionLabel={(option: any) => <span>{option.label}</span>}
                  value={
                    conditions && filters
                      ? (conditions.find((c) => c.value === filters.passedEqualityKey) ?? null)
                      : null
                  }
                  onChange={(selected) => {
                    const value = selected ? (selected as any).value : '';
                    setFilters &&
                      setFilters((prev) => ({
                        ...prev,
                        tabId: tabItem.id,
                        passedEqualityKey: value,
                      }));
                  }}
                  placeholder={t('exams.totalLeaderBoard.filters.statusPlaceholder')}
                />
              </Col>
              <Col sm="4">
                <Input
                  type="number"
                  value={filters.passedExamsQty}
                  onChange={(e) =>
                    setFilters &&
                    setFilters((prev) => ({
                      ...prev,
                      tabId: tabItem.id,
                      passedExamsQty: e.target.value,
                    }))
                  }
                />
              </Col>
            </div>
          )}
        </>
      </Col>
      <Col sm="3">
        <p>{t('exams.totalLeaderBoard.filters.score')}</p>
        {/** Score filters: mirror Passed Exams controls but operate on `filters.score`, `filters.scoreEqualityKey`, `filters.scoreQty` */}
        {getPassedScoreRadios(t).map((input) => (
          <FormGroup check key={`score-${input.id}`} className={`radio radio-'primary'`}>
            <Input
              type="radio"
              name={`${input.name}-score-${tabItem.id}`}
              id={`score-radio-${tabItem.id}-${input.id}`}
              value={input.value}
              checked={filters && filters.score === input.value}
              onChange={() => {
                if (input.value === 'allScores') {
                  setFilters &&
                    setFilters((prev) => ({
                      ...prev,
                      tabId: tabItem.id,
                      score: 'allScores',
                      scoreQty: '0',
                      scoreEqualityKey: 'equal',
                    }));
                  return;
                }
                setFilters &&
                  setFilters((prev) => ({
                    ...prev,
                    tabId: tabItem.id,
                    score: input.value,
                    scoreQty: '0',
                  }));
              }}
            />
            <Label check htmlFor={`score-radio-${tabItem.id}-${input.id}`}>
              {input.text}
            </Label>
          </FormGroup>
        ))}

        {filters && filters.tabId === tabItem.id && filters.score !== 'allScores' && (
          <div className="d-flex gap-3 pe-3">
            <Col sm="8">
              <Select<{ value: string; label: string }, false>
                options={conditions}
                closeMenuOnSelect={true}
                className="custom-selector no_placeholder"
                classNamePrefix={'custom-selector'}
                // styles={colourStyles}
                formatOptionLabel={(option: any) => <span>{option.label}</span>}
                value={
                  conditions && filters
                    ? (conditions.find((c) => c.value === filters.scoreEqualityKey) ?? null)
                    : null
                }
                onChange={(selected) => {
                  const value = selected ? (selected as any).value : '';
                  setFilters &&
                    setFilters((prev) => ({
                      ...prev,
                      tabId: tabItem.id,
                      scoreEqualityKey: value,
                    }));
                }}
                placeholder={t('exams.totalLeaderBoard.filters.statusPlaceholder')}
              />
            </Col>
            <Col sm="4">
              <Input
                type="number"
                value={filters.scoreQty}
                onChange={(e) => {
                  return (
                    setFilters &&
                    setFilters((prev) => ({
                      ...prev,
                      tabId: tabItem.id,
                      scoreQty: e.target.value,
                    }))
                  );
                }}
              />
            </Col>
          </div>
        )}
      </Col>

      <Col sm="3">
        <Label>{t('exams.totalLeaderBoard.filters.status')}</Label>
        <Select
          options={statuses}
          isMulti
          closeMenuOnSelect={false}
          styles={colourStyles}
          components={{ Option: SelectOption, MultiValue: MultiValueContainer }}
          className="custom-selector custom-scrollbar"
          classNamePrefix={'custom-selector'}
          // formatOptionLabel={(option: any, state: any) => (
          //   <div style={{ display: 'flex', alignItems: 'center' }}>
          //     <span
          //       style={{
          //         background: 'transparent' ,
          //         // color:  getContrastColor(option.colorCode) ,
          //         padding: '2px 6px',
          //         borderRadius: 3,
          //       }}
          //     >
          //       {option.label}
          //     </span>
          //   </div>
          // )}
          value={
            filters && statuses && statuses.length
              ? statuses.filter((s) => filters.statusFilter.includes(s.value))
              : []
          }
          onChange={(selected: any) => {
            const values = (selected || []).map((s: any) => s.value);
            setFilters &&
              setFilters((prev) => ({
                ...prev,
                tabId: tabItem.id,
                statusFilter: values,
              }));
          }}
          placeholder={t('exams.totalLeaderBoard.filters.statusPlaceholder')}
        />
      </Col>
      <Col sm="3" className="d-flex align-items-start">
        <Button color="primary" className="ms-auto" onClick={applyFilters}>
          {t('exams.totalLeaderBoard.filters.applyFilters')}
        </Button>
      </Col>
    </Row>
  );
};
